/**
 * useSidebarIntegration Hook
 *
 * Handles sidebar state updates based on the current timeslot selection.
 * Integrates with the MIA-Dashboard sidebar system to show the appropriate
 * sidebar panel (Calendar, Event Manager, or Appointment Details) based on
 * the current selection state.
 *
 * @requirements 4.1, 4.2, 4.3, 4.5 - Sidebar Integration
 */

import { useEffect } from 'react'
import { useMiscDispatch } from 'services/Misc'
import {
  setAppointmentSideBar,
  AppointmentSideBar,
  appointmentSideBarDefault,
} from 'services/Misc/appointmentSideBar'
import { TimeSlot } from '../types'
import { determineSidebarState } from './sidebarLogic'

// Re-export pure functions and types for external use
export { determineSidebarState } from './sidebarLogic'
export type { SidebarState } from './sidebarLogic'

/**
 * Props for the useSidebarIntegration hook
 */
export interface UseSidebarIntegrationProps {
  /** Array of currently selected timeslots */
  selectedSlots: TimeSlot[]
}

/**
 * Custom hook for managing sidebar state based on timeslot selection.
 *
 * This hook provides:
 * - Automatic sidebar updates when selection changes
 * - Default sidebar (Calendar) when no slots are selected
 * - Event Manager sidebar when open slots are selected
 * - Appointment Details sidebar when a single booked slot is selected
 * - Cleanup on unmount to reset sidebar to default state
 *
 * @param props - Hook configuration with selectedSlots
 *
 * Requirement 4.1: When no timeslots are selected, show default calendar sidebar
 * Requirement 4.2: When one or more available timeslots are selected, show Event Manager
 * Requirement 4.3: When a single occupied timeslot is clicked, show Appointment Details
 * Requirement 4.5: When the component unmounts, reset sidebar to default state
 */
export function useSidebarIntegration({ selectedSlots }: UseSidebarIntegrationProps): void {
  const miscDispatch = useMiscDispatch()

  useEffect(() => {
    const sidebarState = determineSidebarState(selectedSlots)

    switch (sidebarState.type) {
      case 'default':
        setAppointmentSideBar(miscDispatch, appointmentSideBarDefault)
        break
      case 'appointmentDetails':
        setAppointmentSideBar(miscDispatch, {
          type: AppointmentSideBar.AppointmentDetails,
          appointmentDetailsSideBarProps: {
            registrationId: sidebarState.registrationId,
          },
        })
        break
      case 'eventManager':
        setAppointmentSideBar(miscDispatch, {
          type: AppointmentSideBar.EventManager,
          eventManagerSideBarProps: {
            timeSlots: sidebarState.timeSlots,
          },
        })
        break
    }
  }, [selectedSlots, miscDispatch])

  // Cleanup on unmount - reset sidebar to default state
  // Requirement 4.5
  useEffect(() => {
    return () => {
      setAppointmentSideBar(miscDispatch, appointmentSideBarDefault)
    }
  }, [miscDispatch])
}
